import { Elements } from '@stripe/react-stripe-js';
import {
  loadStripe,
  type Appearance,
  type StripeElementsOptions,
} from '@stripe/stripe-js';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { getCustomerSessionClientSecret } from './apiHelpers';

const getApiKey = () => {
  const apiKey = process.env.STRIPE_API_PUBLISHABLE_KEY;
  if (!apiKey) {
    throw new Error('Stripe Api Publishable Key is required');
  }
  return apiKey;
};

const promise = loadStripe(getApiKey(), { apiVersion: '2024-04-10' });

interface StripeElementsProps {
  children: ReactNode;
}

interface NewStripeElementsProps {
  appearance?: Appearance;
  children: ReactNode;
  amount?: number;
  /** Restrict which payment methods Elements renders (e.g. card-only for offers). */
  paymentMethodTypes?: string[];
  /**
   * Offer flow: collect a ConfirmationToken via Payment Element (required when
   * saving cards). Server confirms it with a SetupIntent.
   */
  paymentMethodCreation?: 'manual';
}

export const StripeElements = ({ children }: StripeElementsProps) => (
  <Elements stripe={promise}>{children}</Elements>
);

const defaultOptions: StripeElementsOptions = {
  appearance: {
    labels: 'floating',
    rules: {
      '.CheckboxLabel': { color: '#fff' },
      '.Input:focus': {
        border: '1px solid #b69967',
      },
    },
    variables: {
      colorBackground: '#18202d',
      colorText: 'white',
      tabIconColor: 'white',
    },
  },
  currency: 'usd',
  setup_future_usage: 'off_session',
};

// Will replace StripeElements so rename it, probably to StripeElementsContext for future use
export const NewStripeElements = ({
  appearance,
  children,
  amount,
  paymentMethodTypes,
  paymentMethodCreation,
}: NewStripeElementsProps) => {
  const [customerSessionClientSecret, setCustomerSessionClientSecret] =
    useState<string>();

  const fetchCustomerSessionClientSecret = async () => {
    const newCustomerSessionClientSecret =
      await getCustomerSessionClientSecret();

    setCustomerSessionClientSecret(newCustomerSessionClientSecret);
  };

  useEffect(() => {
    void fetchCustomerSessionClientSecret();
  }, []);

  const options: StripeElementsOptions = useMemo(() => {
    const isOfferFlow = paymentMethodCreation === 'manual';
    const { setup_future_usage: _unused, ...withoutSetupFutureUsage } =
      defaultOptions;

    return {
      ...(isOfferFlow ? withoutSetupFutureUsage : defaultOptions),
      appearance: appearance ?? defaultOptions.appearance,
      // ConfirmationTokens from setup mode must be confirmed with a SetupIntent.
      mode: isOfferFlow || !amount ? 'setup' : 'payment',
      ...(!isOfferFlow && amount ? { amount } : {}),
      ...(paymentMethodTypes && { payment_method_types: paymentMethodTypes }),
      ...(paymentMethodCreation && { paymentMethodCreation }),
      customerSessionClientSecret,
    };
  }, [
    amount,
    appearance,
    customerSessionClientSecret,
    paymentMethodTypes,
    paymentMethodCreation,
  ]);

  return (
    customerSessionClientSecret && (
      <Elements stripe={promise} options={options}>
        {children}
      </Elements>
    )
  );
};
